import {
    LogManager
} from './log-manager'
import { 
    LogType, 
    AnalyticsEventBaseType, 
    AnalyticsEventType,
    BidOriginSourceType,
    ImpressionType,
    ComponentSourceType,
    PlayerEventLogFrequency,
    OsdBidOrderOption
} from '../../common/schemas/enums'
import {
    postPlayerEvent
} from "./api";
import { 
    UserRegionInfo,
    UserDeviceInfo
} from "../../common/schemas/public";

interface CXEvent{
    userAgent: string,
    sessionId: string,
    groupId: string,
    domain: string,
    pageUrl: string,
    videoUrl?: string,
    videoSessionId?: string,
    hasAdkId: boolean,
    playerMode: string,
    eventType: string,
    subType: string,
    eventOriginSource: BidOriginSourceType,
    componentSource: ComponentSourceType,
    regionInfo: UserRegionInfo,
    deviceInfo: UserDeviceInfo,
    logicalVideoId?: string,
    mediaId?: string,
    eventOrder: number,
    activeWindow: boolean,
    data: BidData|BidResponseData|InsertData|VideoData|OverlayData|FetchMomentsData|BidEnrichmentData
}

interface ComponentDimensions{
    width: number,
    height: number
}

export interface FetchMomentsData{
    groupId: string,
    videoUrl: string,
    duration: number
}

export interface BidData{
    overlayTime: number,
    activeOverlayTime: number,
    totalBidsCount: number,
    adOpportunityBidCount: number,
    adOpportunityId: string,
    bidRequestId: string,
    componentDimensions: ComponentDimensions,
    bannerSizeRequested?: string,
    nativeImageSizeRequested?: string,
    debug?: {[key: string]:any},
    videoTime?: number,
    containerViewability: number,
}

export interface PrebidData{
    overlayTime: number,
    activeOverlayTime: number,
    totalBidsCount: number,
    adOpportunityBidCount: number,
    adOpportunityId: string,
    bidRequestId: string,
    componentDimensions: ComponentDimensions,
    bannerSizeRequested?: string,
    debug?: {[key: string]:any},
    videoTime?: number,
    containerViewability: number,
    osdBidType?: OsdBidOrderOption
}

export interface GAMData{
    overlayTime: number,
    activeOverlayTime: number,
    totalBidsCount: number,
    adOpportunityBidCount: number,
    adOpportunityId: string,
    bidRequestId: string,
    componentDimensions: ComponentDimensions,
    bannerSizeRequested?: string,
    debug?: {[key: string]:any},
    videoTime?: number,
    containerViewability: number,
}

export interface TAMData{
    overlayTime: number,
    activeOverlayTime: number,
    totalBidsCount: number,
    adOpportunityBidCount: number,
    adOpportunityId: string,
    bidRequestId: string,
    componentDimensions: ComponentDimensions,
    bannerSizeRequested?: string,
    debug?: {[key: string]:any},
    videoTime?: number,
    containerViewability: number,
}

export interface BidResponseData{
    overlayTime: number,
    activeOverlayTime: number,
    totalBidsCount: number,
    adOpportunityBidCount: number,
    adOpportunityId: string,
    bidRequestId: string,
    bidCpm: number,
    cid: string,
    crid: string,
    seatId: string,
    adId: string,
    bidId: string,
    impressionType: string,
    componentDimensions: ComponentDimensions,
    bannerSize?: string,
    debug?: {[key: string]:any},
    videoTime?: number,
    containerViewability: number,
}

export interface VideoTimeData{
    timeReached?: string,
    quartileCompleted?: string
}

interface InsertData{
    insertType: string,
    insertId: string,
    overlayTime: number,
    activeOverlayTime: number,
    videoTime: number,
    containerViewability: number,
    insertViewability?: number,
    insertDisplayedTotalTime?: number,
    insertInViewTotalTime?: number,
    insertAverageViewabilityValue?: number,
    bannerSize?: string,
    bidDirectData?: {}
    bidRequestId: string,
    adOpportunityId: string,
    impressionType: string,
    seat: string,
    cid: string,
    crid: string,
    cpm?: number,
    adm?: any,
    debug?: {[key: string]:any}
    isBackfill?: boolean
}

interface VideoData{
    overlayTime: number,
    activeOverlayTime: number,
    videoTime: number,
    containerViewability: number,
    timeReached?: string,
    quartileCompleted?: string,
    componentDimensions: ComponentDimensions,
    debug?: {[key: string]:any}
}

interface OverlayData{
    overlayTime: number,
    activeOverlayTime: number,
    containerViewability: number,
    timeReached?: string,
    fps?: number,
    debug?: {[key: string]:any}
}

interface BidEnrichmentData{
    debug?: {[key: string]:any}
}

export class AnalyticsEventDispatcher {
    private logManager: LogManager = null;
    private locationInformation: UserRegionInfo = null;
    private deviceInformation: UserDeviceInfo = null;
    private groupId: string = null;
    private sessionId: string = null;
    private logicalVideoId: string = null;
    private mediaId: string = null;
    private videoUrl: string = null;
    private videoSessionId: string = null;
    private eventCount: number = 1;
    private eventComponentSource: ComponentSourceType = null;
    private excludeEvents: {eventType: AnalyticsEventBaseType, subType: AnalyticsEventType}[] = [];
    private eventsSentTimestamps: number[] = [];
    private shutdownFunction: Function;
    private shutdown: boolean = false;
    private allowedEventsPerMinute: number = 150;
    private bidResponseIds: string[] = [];
    private analyticsTier: PlayerEventLogFrequency = PlayerEventLogFrequency.ALL;

    constructor(logManager: LogManager, shutdownFunction: Function, allowedEPM: number){
        this.logManager = logManager;
        this.eventCount = 1;
        this.shutdownFunction = shutdownFunction;
        this.allowedEventsPerMinute = allowedEPM;
        this.logManager.evaluateDebugLog(`Events dispatcher created with ${this.allowedEventsPerMinute} events per minute allowed.`, [LogType.CXO, LogType.QA]);
    }

    assignEventsLocationData(ld:UserRegionInfo){
        this.locationInformation = ld;
    }

    assignEventsDeviceData(dd:UserDeviceInfo){
        this.deviceInformation = dd;
    }

    assignEventsComponentSource(componentSource: ComponentSourceType) {
        this.eventComponentSource = componentSource;
    }

    assignEventsGroupId(gid:string){
        this.groupId = gid;
    }

    assignEventsSessionId(sid:string){
        this.sessionId = sid;
    }

    assignLogicalVideoId(lvid:string){
        this.logicalVideoId = lvid;
    }

    assignMediaId(mid:string){
        this.mediaId = mid;
    }

    assignVideoUrl(vidUrl:string, vidUrlId: string){
        this.videoUrl = vidUrl;
        this.videoSessionId = vidUrlId;
    }

    assignPlayerEventFrequency(mode:PlayerEventLogFrequency){
        this.analyticsTier = mode;
        this.logManager.evaluateDebugLog([`Setting player event frequency to ${this.analyticsTier}`], [LogType.CXO, LogType.QA]);
        switch(this.analyticsTier){
            case PlayerEventLogFrequency.LOW:
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.OVERLAY, subType: AnalyticsEventType.OVERLAY_GROUPID_ZONE_MISMATCH});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.OVERLAY, subType: AnalyticsEventType.OVERLAY_GROUP_CONFIGS});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.TAM, subType: AnalyticsEventType.TAM_REQUEST});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.TAM, subType: AnalyticsEventType.TAM_RESPONSE});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.PREBID, subType: AnalyticsEventType.PREBID_REQUEST});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.PREBID, subType: AnalyticsEventType.PREBID_RESPONSE});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.GAM, subType: AnalyticsEventType.GAM_RESPONSE_EMPTY});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.BID, subType: AnalyticsEventType.BID_NO_REQUEST});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.OVERLAY, subType: AnalyticsEventType.CLS_INIT});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.OVERLAY, subType: AnalyticsEventType.CLS_SHIFT_OBSERVED});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.INSERT, subType: AnalyticsEventType.INSERT_CREATED});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.VIDEO, subType: AnalyticsEventType.VIDEO_STARTED});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.VIDEO, subType: AnalyticsEventType.VIDEO_ENDED});
            case PlayerEventLogFrequency.MEDIUM:
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.OVERLAY, subType: AnalyticsEventType.OVERLAY_CAPTURE_VIDEO_POSITION});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.OVERLAY, subType: AnalyticsEventType.OVERLAY_CAPTURE_AD_CONCENTRATION});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.VIDEO, subType: AnalyticsEventType.VIDEO_PLAY});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.VIDEO, subType: AnalyticsEventType.VIDEO_RESTARTED});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.VIDEO, subType: AnalyticsEventType.VIDEO_PAUSE});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.VIDEO, subType: AnalyticsEventType.VIDEO_SRC_CHANGED});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.VIDEO, subType: AnalyticsEventType.VIDEO_QUARTILE_COMPLETE});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.INSERT, subType: AnalyticsEventType.INSERT_INITIALIZED});
            case PlayerEventLogFrequency.HIGH:
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.OVERLAY, subType: AnalyticsEventType.OVERLAY_TIME_REACHED});
                this.excludeEvents.push({eventType: AnalyticsEventBaseType.VIDEO, subType: AnalyticsEventType.VIDEO_TIME_REACHED});
                break;
            case PlayerEventLogFrequency.ALL:
                this.excludeEvents.splice(0, this.excludeEvents.length);
                break;
        }
    }

    sendEventObject(eventType: AnalyticsEventBaseType, eventName: AnalyticsEventType, pageUrl: string, hasAdkId: boolean, playerMode: string, originSource: BidOriginSourceType, data: BidData|BidResponseData|InsertData|VideoData|OverlayData|FetchMomentsData|BidEnrichmentData) {   
        try{
            const domain = new URL(pageUrl).hostname;
            const cxEvent: CXEvent = {
                playerMode: playerMode,
                groupId: this.groupId,
                hasAdkId: hasAdkId,
                eventType: eventType,
                subType: eventName,
                eventOriginSource: originSource,
                componentSource: this.eventComponentSource,
                data: data,
                domain: domain,
                pageUrl: pageUrl,
                sessionId: this.sessionId,
                userAgent: window.navigator.userAgent,
                regionInfo: this.locationInformation,
                deviceInfo: this.deviceInformation,
                eventOrder: this.eventCount++,
                activeWindow: !document.hidden
            }
    
            if (this.eventComponentSource == ComponentSourceType.OVERLAY) {
                cxEvent.videoUrl = this.videoUrl || null;
                cxEvent.videoSessionId = this.videoSessionId || null;
                cxEvent.logicalVideoId = this.logicalVideoId || null;
                cxEvent.mediaId = this.mediaId || null;
            }
    
            if(!!this.excludeEvents.find(excl => (excl.eventType === eventType && excl.subType === eventName)) || this.analyticsTier === PlayerEventLogFrequency.NONE){
                this.logManager.evaluateDebugLog([`Not sending event - event in excluded list: ${eventType} ${eventName}`], [LogType.CXO, LogType.QA]);
            }else if(eventType === AnalyticsEventBaseType.BID && (eventName === AnalyticsEventType.BID_RESPONSE || eventName === AnalyticsEventType.BID_RESPONSE_EMPTY)){
                const bidId: string = (data as BidData).bidRequestId || '';
                if(!this.bidResponseIds.includes(bidId)){
                    this.logManager.evaluateDebugLog([`Sending ${eventType} (${originSource}): ${eventName} event to player-event-api :: ${JSON.stringify(cxEvent.data)}`,cxEvent], [LogType.CXO, LogType.QA]);
                    this.bidResponseIds.push(bidId);
                    postPlayerEvent(cxEvent);
                }else{
                    this.logManager.evaluateDebugLog([`Not sending event: ${eventType} ${eventName} - bid response event already sent for bid id ${bidId}.`], [LogType.CXO, LogType.QA]);
                }
            }else{
                this.logManager.evaluateDebugLog([`Sending ${eventType} (${originSource}): ${eventName} event to player-event-api :: ${JSON.stringify(cxEvent.data)}`,cxEvent], [LogType.CXO, LogType.QA]);
                postPlayerEvent(cxEvent);
            }
    
            this.addEventTimestamp();
        } catch(err){
            this.logManager.evaluateDebugLog([`Error sending event: ${eventType} ${eventName}`, err], [LogType.CXO, LogType.QA]);
        }
    }


    addEventTimestamp(){
        const nowTime = (new Date().getTime());
        this.eventsSentTimestamps.push(nowTime);
        for(var i = 0; i < this.eventsSentTimestamps.length; i++){
            if(this.eventsSentTimestamps[i] + 60000 < nowTime){
                this.eventsSentTimestamps.splice(i, 1);
                i--;
            }else{
                break;
            }
        }
        if(this.eventsSentTimestamps.length > this.allowedEventsPerMinute && !this.shutdown){
            this.logManager.evaluateDebugLog(`Events per minute has exceeded set limit of (${this.allowedEventsPerMinute}). Will remove client.`, [LogType.CXO, LogType.QA]);
            this.shutdown = true;
            this.shutdownFunction();
        }
    }

    sendBidEvent(eventName: AnalyticsEventType, originSource: BidOriginSourceType, pageUrl: string, hasAdkId: boolean, playerMode: string, bidData: BidData) {
        this.sendEventObject(AnalyticsEventBaseType.BID, eventName, pageUrl, hasAdkId, playerMode, originSource, bidData);
    }

    sendBidResponseEvent(eventName: AnalyticsEventType, originSource: BidOriginSourceType, pageUrl: string, hasAdkId: boolean, playerMode: string, responseData: BidResponseData) {
        this.sendEventObject(AnalyticsEventBaseType.BID, eventName, pageUrl, hasAdkId, playerMode, originSource, responseData);
    }

    sendPrebidEvent(eventName: AnalyticsEventType, originSource: BidOriginSourceType, pageUrl: string, hasAdkId: boolean, playerMode: string, prebidData: PrebidData) {
        this.sendEventObject(AnalyticsEventBaseType.PREBID, eventName, pageUrl, hasAdkId, playerMode, originSource, prebidData);
    }

    sendGAMEvent(eventName: AnalyticsEventType, originSource: BidOriginSourceType, pageUrl: string, hasAdkId: boolean, playerMode: string, gamData: GAMData) {
        this.sendEventObject(AnalyticsEventBaseType.GAM, eventName, pageUrl, hasAdkId, playerMode, originSource, gamData);
    }

    sendTAMEvent(eventName: AnalyticsEventType, originSource: BidOriginSourceType, pageUrl: string, hasAdkId: boolean, playerMode: string, tamData: TAMData) {
        this.sendEventObject(AnalyticsEventBaseType.TAM, eventName, pageUrl, hasAdkId, playerMode, originSource, tamData);
    }

    sendFetchMomentsEvent(eventName: AnalyticsEventType, originSource: BidOriginSourceType, pageUrl: string, hasAdkId: boolean, playerMode: string, fetchMomentsData: FetchMomentsData) {
        this.sendEventObject(AnalyticsEventBaseType.OVERLAY, eventName, pageUrl, hasAdkId, playerMode, originSource, fetchMomentsData);
    }

    sendInsertEvent(eventName: AnalyticsEventType, originSource: BidOriginSourceType, seat: string, cid: string, crid: string, pageUrl: string, hasAdkId: boolean, playerMode: string, vidTime: number, overlayTime: number, activeOverlayTime: number, insertId: string, insertType: string, bannerSize: string, adOpportunityId: string, bidRequestId: string, containerViewability: number, debugData?: {[key: string]:any}, insertViewability?: number, insertLifetime?: number, insertViewabilityTime?: number, insertAverageViewabilityValue?: number) {
        const insertData: InsertData = {
            insertType: insertType.toUpperCase() || 'null',
            overlayTime: overlayTime/1000,
            activeOverlayTime: activeOverlayTime/1000,
            videoTime: vidTime,
            insertId: insertId,
            insertViewability: 0,
            containerViewability: Math.ceil(containerViewability*100),
            bidRequestId: bidRequestId ?? 'null',
            adOpportunityId: adOpportunityId ?? 'null',
            impressionType: ImpressionType.NATIVE,
            seat: seat,
            cid: cid,
            crid: crid
        }
        if(typeof debugData !== 'undefined' && debugData !== null){
            insertData.debug = debugData;
        }
        if(insertViewability != null){
            insertData.insertViewability = Math.ceil(insertViewability*100);
        }
        if(insertLifetime != null){
            insertData.insertDisplayedTotalTime = insertLifetime;
        }
        if(insertViewabilityTime != null){
            insertData.insertInViewTotalTime = insertViewabilityTime;
        }
        if(typeof bannerSize !== 'undefined' && bannerSize !== null){
            insertData.bannerSize = bannerSize;
            insertData.impressionType = ImpressionType.DISPLAY;
        }
        if(typeof insertAverageViewabilityValue !== 'undefined' && insertAverageViewabilityValue !== null){
            insertData.insertAverageViewabilityValue = insertAverageViewabilityValue || null;
        }
        this.sendEventObject(AnalyticsEventBaseType.INSERT, eventName, pageUrl, hasAdkId, playerMode, originSource, insertData);
    }

    sendInsertFailedEvent(eventName: AnalyticsEventType, originSource: BidOriginSourceType, seat: string, cid: string, crid: string, pageUrl: string, hasAdkId: boolean, playerMode: string, vidTime: number, overlayTime: number, activeOverlayTime: number, insertId: string, insertType: string, bannerSize: string, adOpportunityId: string, bidRequestId: string, adm:any, containerViewability: number, debugData?: {[key: string]:any}, insertViewability?: number) {
        const insertData: InsertData = {
            insertType: insertType.toUpperCase() || 'null',
            overlayTime: overlayTime/1000,
            activeOverlayTime: activeOverlayTime/1000,
            videoTime: vidTime,
            insertId: insertId,
            insertViewability: 0,
            containerViewability: Math.ceil(containerViewability*100),
            bidRequestId: bidRequestId ?? 'null',
            adOpportunityId: adOpportunityId ?? 'null',
            impressionType: ImpressionType.NATIVE,
            adm: adm,
            seat: seat,
            cid: cid,
            crid: crid
        }
        if(typeof debugData !== 'undefined' && debugData !== null){
            insertData.debug = debugData;
        }
        if(insertViewability != null){
            insertData.insertViewability = Math.ceil(insertViewability*100);
        }
        if(typeof bannerSize !== 'undefined' && bannerSize !== null){
            insertData.bannerSize = bannerSize;
            insertData.impressionType = ImpressionType.DISPLAY;
        }
        this.sendEventObject(AnalyticsEventBaseType.INSERT, eventName, pageUrl, hasAdkId, playerMode, originSource, insertData);
    }

    sendInsertImpressionEvent(eventName: AnalyticsEventType, originSource: BidOriginSourceType, seat: string, cid: string, crid: string, pageUrl: string, hasAdkId: boolean, playerMode: string, vidTime: number, overlayTime: number, activeOverlayTime: number, insertId: string, insertType: string, bannerSize: string, adOpportunityId: string, bidRequestId: string, containerViewability: number, insertViewability: number, cpm: number, debugData?: {[key: string]:any}) {
        const insertData: InsertData = {
            insertType: insertType.toUpperCase() || 'null',
            overlayTime: overlayTime/1000,
            activeOverlayTime: activeOverlayTime/1000,
            videoTime: vidTime,
            insertId: insertId,
            insertViewability: 0,
            containerViewability: Math.ceil(containerViewability*100),
            bidRequestId: bidRequestId ?? 'null',
            adOpportunityId: adOpportunityId ?? 'null',
            cpm: cpm ?? null,
            impressionType: ImpressionType.NATIVE,
            seat: seat,
            cid: cid,
            crid: crid
        }
        if(typeof debugData !== 'undefined' && debugData !== null){
            insertData.debug = debugData;
        }
        if(insertViewability != null){
            insertData.insertViewability = Math.ceil(insertViewability*100);
        }
        if(typeof bannerSize !== 'undefined' && bannerSize !== null){
            insertData.bannerSize = bannerSize;
            insertData.impressionType = ImpressionType.DISPLAY;
        }
        this.sendEventObject(AnalyticsEventBaseType.INSERT, eventName, pageUrl, hasAdkId, playerMode, originSource, insertData);
    }

    sendVideoEvent(eventName: AnalyticsEventType, originSource: BidOriginSourceType, pageUrl: string, hasAdkId: boolean, playerMode: string, videoTime: number, overlayTime: number, activeOverlayTime: number, containerViewability: number, compDimensions: ComponentDimensions, videoTimeData?: VideoTimeData, debug?: {[key:string]: any}) {
        let videoData: VideoData ={
            overlayTime: overlayTime/1000,
            activeOverlayTime: activeOverlayTime/1000,
            videoTime: videoTime,
            componentDimensions: compDimensions,
            containerViewability: Math.ceil(containerViewability*100)
        }
        if(!!videoTimeData?.timeReached){
            videoData.timeReached = videoTimeData.timeReached;
        }
        if(!!videoTimeData?.quartileCompleted){
            videoData.quartileCompleted = videoTimeData.quartileCompleted;
        }
        if(typeof debug !== 'undefined' && debug != null){
            videoData.debug = debug;
        }
        this.sendEventObject(AnalyticsEventBaseType.VIDEO, eventName, pageUrl, hasAdkId, playerMode, originSource, videoData);
    }

    sendOverlayEvent(eventName: AnalyticsEventType, originSource: BidOriginSourceType, pageUrl: string, hasAdkId: boolean, playerMode: string, overlayTime: number, activeOverlayTime: number, containerViewability: number, debugData?: {[key:string]: any}) {
        let overlayData: OverlayData = {
            overlayTime: overlayTime/1000,
            activeOverlayTime: activeOverlayTime/1000,
            containerViewability: Math.ceil(containerViewability*100)
        }
        if(typeof debugData !== 'undefined' && debugData !== null){
            overlayData.debug = debugData;
        }
        this.sendEventObject(AnalyticsEventBaseType.OVERLAY, eventName, pageUrl, hasAdkId, playerMode, originSource, overlayData);
    }

    sendOverlayTimeEvent(eventName: AnalyticsEventType, originSource: BidOriginSourceType, pageUrl: string, hasAdkId: boolean, playerMode: string, overlayTime: number, activeOverlayTime: number, containerViewability: number, frameRate: number, timeReached: string) {
        let overlayData: OverlayData = {
            overlayTime: overlayTime/1000,
            activeOverlayTime: activeOverlayTime/1000,
            containerViewability: Math.ceil(containerViewability*100),
            timeReached:  timeReached,
            fps: parseFloat(frameRate.toFixed(2))
        }
        this.sendEventObject(AnalyticsEventBaseType.OVERLAY, eventName, pageUrl, hasAdkId, playerMode, originSource, overlayData);
    }

    sendBidEnrichmentEvent(eventName: AnalyticsEventType, originSource: BidOriginSourceType, pageUrl: string, debugData?: {[key:string]: any}) {
        let bidEnrichData: BidEnrichmentData = null;
        
        if(typeof debugData !== 'undefined' && debugData !== null){
            bidEnrichData = {
                debug: debugData
            }
        }
        this.sendEventObject(AnalyticsEventBaseType.BID_ENRICHMENT, eventName, pageUrl, false, 'NONE', originSource, bidEnrichData);
    }
}